import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, onboardingComplete, isPasswordRecovery, isStaff, isAdmin, ownsBusiness, isSupportCollaborator, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Show loading spinner while auth or business data is resolving.
  // This prevents routing flashes.
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">Just a moment...</p>
          <p className="text-xs text-muted-foreground">We're setting up your account</p>
        </div>
      </div>
    );
  }

  // If a password recovery flow is active, redirect to the reset password page
  if (isPasswordRecovery) {
    return <Navigate to="/auth/reset-password" replace />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // Access to the dashboard requires ONE of: platform admin, a business owner
  // (their own workspace), staff, or an active Dibs collaborator. A user with
  // none of these is an orphaned staff member whose access was deleted by an
  // admin. NOTE: `ownsBusiness` is deliberately separate from `isAdmin`. Owners
  // were downgraded off the global admin role by the P0 RLS fix, but must still
  // reach their own dashboard/onboarding.
  if (!isAdmin && !ownsBusiness && !isStaff && !isSupportCollaborator) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-heading font-bold">Access Removed</h1>
          <p className="text-muted-foreground">Your access to this dashboard has been removed by the administrator. Please contact them if you believe this is a mistake.</p>
          <Button onClick={async () => { await signOut(); navigate('/auth'); }} variant="default">
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  // Staff users and Dibs collaborators skip onboarding. They don't own a
  // businesses row of their own.
  if (isStaff || isSupportCollaborator) {
    return <>{children}</>;
  }

  const isOnboardingRelated = ["/onboarding", "/enterprise-thanks", "/onboarding-success"].includes(location.pathname);

  if (!onboardingComplete && !isOnboardingRelated) {
    return <Navigate to="/onboarding" replace />;
  }

  if (onboardingComplete && location.pathname === "/onboarding") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
